import { useCallback, useState } from 'react'
import * as R from 'ramda'

import type { Order } from '~/types'
import {
  getOrdersEqual,
  isBundle,
  isWellnessPlanItem,
} from '~/utils/orderUtils'

export const useEstimateSelectedItems = () => {
  const [selectedItems, setSelectedItems] = useState<Order[]>([])
  const [selectedSubItem, setSelectedSubItem] = useState<Order>()

  const getSelectedItemsHaveOrder = useCallback(
    (order: Order) => selectedItems.some((item) => getOrdersEqual(item, order)),
    [selectedItems],
  )

  const isSelectedItem = useCallback(
    (order: Order) => {
      if (isBundle(order)) {
        return selectedItems.some((item) => item.bundleId === order.bundleId)
      }
      if (isWellnessPlanItem(order)) {
        return selectedItems.every((item) => item.id !== order.id)
      }
      return (
        R.equals(selectedSubItem, order) || getSelectedItemsHaveOrder(order)
      )
    },
    [selectedItems, selectedSubItem, getSelectedItemsHaveOrder],
  )

  const updateSelectedItem = useCallback(
    (order: Order) => {
      const wasSelected = isWellnessPlanItem(order)
        ? !isSelectedItem(order)
        : isSelectedItem(order)

      if (isBundle(order) && wasSelected) {
        setSelectedItems((prev) =>
          prev.filter((item) => item.bundleId !== order.bundleId),
        )
      } else if (wasSelected) {
        setSelectedItems((prev) =>
          prev.filter((item) => !getOrdersEqual(item, order)),
        )
      } else {
        setSelectedItems((prev) => [...prev, order])
      }
      setSelectedSubItem(undefined)
    },
    [isSelectedItem],
  )

  return {
    isSelectedItem,
    selectedSubItem,
    setSelectedSubItem,
    updateSelectedItem,
  }
}
